import React, { useState, useEffect, createContext, useContext } from 'react';

// ── Auth Context ──────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

export function useAuth() { return useContext(AuthContext); }

function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('adtsync_token'));
  const [username, setUsername] = useState(() => localStorage.getItem('adtsync_user'));

  const login = (tok, user) => {
    setToken(tok);
    setUsername(user);
    localStorage.setItem('adtsync_token', tok);
    localStorage.setItem('adtsync_user', user);
  };

  const logout = () => {
    setToken(null);
    setUsername(null);
    localStorage.removeItem('adtsync_token');
    localStorage.removeItem('adtsync_user');
  };

  return (
    <AuthContext.Provider value={{ token, username, login, logout, isAuthed: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── API Helper ────────────────────────────────────────────────────────────────

export function useApi() {
  const { token, logout } = useAuth();

  const call = async (method, path, body) => {
    const res = await fetch(`/api${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (res.status === 401) { logout(); return null; }
    if (!res.ok) throw new Error(await res.text());
    if (res.status === 204) return null;
    return res.json();
  };

  return {
    get: (path) => call('GET', path),
    post: (path, body) => call('POST', path, body),
    put: (path, body) => call('PUT', path, body),
    patch: (path, body) => call('PATCH', path, body),
    del: (path) => call('DELETE', path),
  };
}

// ── Login Page ────────────────────────────────────────────────────────────────

function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      if (!res.ok) { setError('Invalid credentials.'); return; }
      const data = await res.json();
      login(data.token, data.username);
    } catch {
      setError('Login failed. Server unreachable.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.loginWrap}>
      <div style={styles.loginCard}>
        <img src="https://advdigitech.com/wp-content/uploads/2021/04/ADT-logo-black.png"
             alt="ADT Logo" style={styles.loginLogo} />
        <h2 style={styles.loginTitle}>ADTSync Admin</h2>
        <form onSubmit={handleSubmit}>
          <input style={styles.input} placeholder="Username" value={username}
                 onChange={e => setUsername(e.target.value)} required />
          <input style={styles.input} type="password" placeholder="Password"
                 value={password} onChange={e => setPassword(e.target.value)} required />
          {error && <div style={styles.error}>{error}</div>}
          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Shell / Nav ───────────────────────────────────────────────────────────────

function Shell({ children, page, setPage }) {
  const { username, logout } = useAuth();
  const nav = [
    { id: 'clients', label: '👥 Clients' },
    { id: 'files', label: '🗂 File Manager' },
    { id: 'settings', label: '⚙️ Settings' },
  ];

  return (
    <div style={styles.shell}>
      <div style={styles.sidebar}>
        <img src="https://advdigitech.com/wp-content/uploads/2021/04/ADT-logo-black.png"
             alt="ADT" style={styles.sidebarLogo} />
        <div style={styles.sidebarTitle}>ADTSync</div>
        {nav.map(n => (
          <div key={n.id}
               style={{ ...styles.navItem, ...(page === n.id ? styles.navActive : {}) }}
               onClick={() => setPage(n.id)}>
            {n.label}
          </div>
        ))}
        <div style={styles.sidebarFooter}>
          <div style={styles.navUser}>👤 {username}</div>
          <div style={{ ...styles.navItem, color: '#e74c3c' }} onClick={logout}>🚪 Sign Out</div>
        </div>
      </div>
      <div style={styles.main}>{children}</div>
    </div>
  );
}


// ── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = async () => {
    try {
      const d = await api.get('/dashboard');
      setData(d);
      setLastUpdated(new Date());
    } catch {}
  };

  useEffect(() => {
    load();
    const fast = setInterval(load, 8000);   // refresh every 8s
    return () => clearInterval(fast);
  }, []);

  const fmtBytes = (b) => {
    if (!b) return '0 B';
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b/1024).toFixed(1)} KB`;
    if (b < 1073741824) return `${(b/1048576).toFixed(1)} MB`;
    return `${(b/1073741824).toFixed(2)} GB`;
  };

  const fmtEta = (secs) => {
    if (!secs) return null;
    if (secs < 60) return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs/60)}m ${secs%60}s`;
    return `${Math.floor(secs/3600)}h ${Math.floor((secs%3600)/60)}m`;
  };

  if (!data) return (
    <div style={db.loadWrap}>
      <div style={db.loadDot} /><div style={{...db.loadDot, animationDelay:'0.15s'}} /><div style={{...db.loadDot, animationDelay:'0.3s'}} />
    </div>
  );

  const totalFilesToday = data.mappingStats.reduce((s,m) => s + m.filesToday, 0);
  const totalFilesWeek  = data.mappingStats.reduce((s,m) => s + m.filesWeek, 0);
  const totalBytesToday = data.mappingStats.reduce((s,m) => s + m.bytesToday, 0);
  const totalBytesWeek  = data.mappingStats.reduce((s,m) => s + m.bytesWeek, 0);
  const uncQueue        = data.mappingStats.reduce((s,m) => s + m.uncPending, 0);
  const mismatches      = data.mappingStats.filter(m => m.diskManifestMismatch).length;

  return (
    <div style={db.wrap}>
      <div style={db.header}>
        <span style={db.title}>Dashboard</span>
        {lastUpdated && <span style={db.updated}>Updated {lastUpdated.toLocaleTimeString()}</span>}
      </div>

      {/* Summary cards */}
      <div style={db.cardRow}>
        <SummaryCard icon="📤" label="Files Today" value={totalFilesToday} sub={`${totalFilesWeek} this week`} color="#3498db" />
        <SummaryCard icon="💾" label="Data Today"  value={fmtBytes(totalBytesToday)} sub={`${fmtBytes(totalBytesWeek)} this week`} color="#9b59b6" />
        <SummaryCard icon="⏫" label="UNC Queue"   value={uncQueue} sub="pending export" color={uncQueue > 0 ? '#e67e22' : '#27ae60'} />
        <SummaryCard icon="⚠️" label="Failures"    value={data.recentFailures.length} sub="recent errors" color={data.recentFailures.length > 0 ? '#e74c3c' : '#27ae60'} />
        {mismatches > 0 && <SummaryCard icon="🔍" label="Mismatches" value={mismatches} sub="disk≠manifest" color="#e74c3c" />}
      </div>

      {/* Active transfers */}
      {data.activeTransfers.length > 0 && (
        <div style={db.section}>
          <div style={db.sectionTitle}>🔄 Active Transfers ({data.activeTransfers.length})</div>
          {data.activeTransfers.map(t => (
            <div key={t.sessionId} style={db.transferCard}>
              <div style={db.transferTop}>
                <span style={db.transferName}>{t.clientName} / {t.mappingLabel}</span>
                <span style={db.transferFile}>{t.relativePath}</span>
                <span style={db.transferMeta}>{fmtBytes(t.bytesReceived)} / {fmtBytes(t.totalSizeBytes)}</span>
                {t.etaSeconds != null && <span style={db.transferEta}>ETA {fmtEta(t.etaSeconds)}</span>}
              </div>
              <div style={db.progressTrack}>
                <div style={{...db.progressFill, width: `${t.progressPct}%`}} />
              </div>
              <div style={db.progressLabel}>{t.progressPct}%{t.isBatch ? ' · batch' : ''}</div>
            </div>
          ))}
        </div>
      )}

      {/* Per-mapping stats */}
      <div style={db.section}>
        <div style={db.sectionTitle}>📊 Mapping Status</div>
        <table style={db.table}>
          <thead>
            <tr style={db.thead}>
              <th style={db.th}>Client</th>
              <th style={db.th}>Mapping</th>
              <th style={{...db.th, textAlign:'center'}}>Status</th>
              <th style={{...db.th, textAlign:'right'}}>Files Today</th>
              <th style={{...db.th, textAlign:'right'}}>Data Today</th>
              <th style={{...db.th, textAlign:'right'}}>Files / Week</th>
              <th style={{...db.th, textAlign:'right'}}>Disk / Manifest</th>
              <th style={{...db.th, textAlign:'center'}}>UNC</th>
            </tr>
          </thead>
          <tbody>
            {data.mappingStats.map(m => (
              <tr key={`${m.clientId}_${m.mappingId}`} style={db.tr}>
                <td style={db.td}>{m.clientName}</td>
                <td style={db.td}>{m.mappingLabel}</td>
                <td style={{...db.td, textAlign:'center'}}>
                  <HeartDot online={m.isOnline} secondsAgo={m.secondsSinceLastSeen} />
                </td>
                <td style={{...db.td, textAlign:'right'}}>{m.filesToday}</td>
                <td style={{...db.td, textAlign:'right'}}>{fmtBytes(m.bytesToday)}</td>
                <td style={{...db.td, textAlign:'right'}}>{m.filesWeek}</td>
                <td style={{...db.td, textAlign:'right'}}>
                  <span style={{color: m.diskManifestMismatch ? '#e74c3c' : 'inherit'}}>
                    {m.diskCount} / {m.manifestCount}
                    {m.diskManifestMismatch && ' ⚠️'}
                  </span>
                </td>
                <td style={{...db.td, textAlign:'center'}}>
                  {!m.uncExportEnabled ? <span style={{color:'#bdc3c7'}}>—</span>
                    : m.uncPending > 0 ? <span style={{color:'#e67e22'}}>⏳ {m.uncPending}</span>
                    : <span style={{color:'#27ae60'}}>✓ {m.uncExported}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent failures */}
      {data.recentFailures.length > 0 && (
        <div style={db.section}>
          <div style={db.sectionTitle}>❌ Recent Failures</div>
          {data.recentFailures.map((f, i) => (
            <div key={i} style={db.failCard}>
              <div style={db.failTop}>
                <span style={db.failClient}>{f.clientName} / {f.mappingLabel}</span>
                <span style={db.failTime}>{new Date(f.failedAtUtc).toLocaleString()}</span>
              </div>
              <div style={db.failFile}>{f.fileName}</div>
              <div style={db.failError}>{f.error}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, sub, color }) {
  return (
    <div style={{...db.card, borderTop: `3px solid ${color}`}}>
      <div style={db.cardIcon}>{icon}</div>
      <div style={{...db.cardValue, color}}>{value}</div>
      <div style={db.cardLabel}>{label}</div>
      <div style={db.cardSub}>{sub}</div>
    </div>
  );
}

function HeartDot({ online, secondsAgo }) {
  const color = online ? '#27ae60' : '#e74c3c';
  const label = online
    ? 'Online'
    : secondsAgo == null ? 'Never seen'
    : secondsAgo < 3600 ? `${Math.floor(secondsAgo/60)}m ago`
    : secondsAgo < 86400 ? `${Math.floor(secondsAgo/3600)}h ago`
    : `${Math.floor(secondsAgo/86400)}d ago`;
  return (
    <span title={label} style={{
      display:'inline-flex', alignItems:'center', gap:5,
      fontSize:12, color, fontWeight:600
    }}>
      <span style={{
        width:10, height:10, borderRadius:'50%', background:color,
        boxShadow: online ? `0 0 0 3px ${color}22` : 'none',
        display:'inline-block'
      }}/>
      {label}
    </span>
  );
}

// Dashboard styles
const db = {
  wrap: { padding: '0 0 32px 0' },
  header: { display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:20 },
  title: { fontSize:22, fontWeight:700, color:'#2c3e50' },
  updated: { fontSize:11, color:'#aaa' },
  cardRow: { display:'flex', gap:14, flexWrap:'wrap', marginBottom:24 },
  card: { background:'#fff', borderRadius:10, padding:'16px 20px', minWidth:150, flex:1,
          boxShadow:'0 2px 8px rgba(0,0,0,0.07)' },
  cardIcon: { fontSize:22, marginBottom:6 },
  cardValue: { fontSize:26, fontWeight:800, lineHeight:1 },
  cardLabel: { fontSize:12, fontWeight:600, color:'#555', marginTop:4, textTransform:'uppercase', letterSpacing:.5 },
  cardSub: { fontSize:11, color:'#aaa', marginTop:2 },
  section: { background:'#fff', borderRadius:10, padding:'16px 20px', marginBottom:18,
             boxShadow:'0 2px 8px rgba(0,0,0,0.07)' },
  sectionTitle: { fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:.8,
                  color:'#7f8c8d', marginBottom:14, borderBottom:'1px solid #f0f0f0', paddingBottom:8 },
  transferCard: { background:'#f7f9fc', borderRadius:8, padding:'10px 14px', marginBottom:8 },
  transferTop: { display:'flex', alignItems:'center', gap:12, marginBottom:6, flexWrap:'wrap' },
  transferName: { fontWeight:700, fontSize:13, color:'#2c3e50' },
  transferFile: { fontSize:12, color:'#555', fontFamily:'monospace', flex:1, overflow:'hidden',
                  textOverflow:'ellipsis', whiteSpace:'nowrap' },
  transferMeta: { fontSize:12, color:'#7f8c8d' },
  transferEta: { fontSize:12, color:'#3498db', fontWeight:600 },
  progressTrack: { height:6, background:'#e0e6ed', borderRadius:3, overflow:'hidden' },
  progressFill: { height:'100%', background:'linear-gradient(90deg,#3498db,#2ecc71)',
                  borderRadius:3, transition:'width 0.5s ease' },
  progressLabel: { fontSize:11, color:'#aaa', marginTop:3 },
  table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
  thead: { background:'#f7f9fc' },
  th: { padding:'8px 12px', textAlign:'left', fontWeight:600, fontSize:11,
        textTransform:'uppercase', letterSpacing:.5, color:'#7f8c8d',
        borderBottom:'2px solid #e0e6ed' },
  tr: { borderBottom:'1px solid #f5f5f5' },
  td: { padding:'9px 12px', color:'#2c3e50' },
  failCard: { background:'#fff5f5', border:'1px solid #fdd', borderRadius:8,
              padding:'10px 14px', marginBottom:8 },
  failTop: { display:'flex', justifyContent:'space-between', marginBottom:4 },
  failClient: { fontWeight:700, fontSize:13, color:'#c0392b' },
  failTime: { fontSize:11, color:'#aaa' },
  failFile: { fontSize:12, fontFamily:'monospace', color:'#555', marginBottom:4 },
  failError: { fontSize:12, color:'#e74c3c' },
  loadWrap: { display:'flex', gap:6, padding:24, justifyContent:'center' },
  loadDot: { width:8, height:8, borderRadius:'50%', background:'#3498db',
             animation:'pulse 0.8s ease-in-out infinite' },
};

// ── Clients Page ──────────────────────────────────────────────────────────────

function ClientsPage() {
  const api = useApi();
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newClientName, setNewClientName] = useState('');
  const [newKey, setNewKey] = useState(null); // {apiKey, aesKey, clientId, mappingId}
  const [loading, setLoading] = useState(true);
  const [showMappingForm, setShowMappingForm] = useState(false);

  const load = async () => {
    setLoading(true);
    const data = await api.get('/clients');
    setClients(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const createClient = async () => {
    if (!newClientName.trim()) return;
    const res = await api.post('/clients', { name: newClientName });
    setNewKey({ type: 'client', apiKey: res.apiKey, clientId: res.id });
    setNewClientName('');
    load();
  };

  const toggleClient = async (c) => {
    await api.patch(`/clients/${c.id}/enabled`, !c.enabled);
    load();
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? This cannot be undone.`)) return;
    await api.del(`/clients/${c.id}`);
    setSelected(null);
    load();
  };

  return (
    <div style={styles.page}>
      <Dashboard />
      <h1 style={styles.pageTitle}>Clients</h1>

      {/* New Key Modal */}
      {newKey && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h3>⚠️ Save These Credentials Now</h3>
            <p style={{ color: '#e74c3c' }}>These values are shown <strong>once only</strong> and cannot be recovered.</p>
            {newKey.apiKey && <KeyDisplay label="API Key" value={newKey.apiKey} />}
            {newKey.aesKey && <KeyDisplay label="AES-256 Key (Base64)" value={newKey.aesKey} />}
            {newKey.clientId && <KeyDisplay label="Client ID" value={newKey.clientId} />}
            {newKey.mappingId && <KeyDisplay label="Mapping ID" value={newKey.mappingId} />}
            <button style={styles.btn} onClick={() => setNewKey(null)}>I have saved these — Close</button>
          </div>
        </div>
      )}

      <div style={styles.twoCol}>
        {/* Left: client list */}
        <div style={styles.listPanel}>
          <div style={styles.addRow}>
            <input style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                   placeholder="New client name" value={newClientName}
                   onChange={e => setNewClientName(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && createClient()} />
            <button style={{ ...styles.btn, marginLeft: 8, padding: '8px 14px' }}
                    onClick={createClient}>Add</button>
          </div>
          {loading ? <Spinner /> : clients.map(c => (
            <div key={c.id}
                 style={{ ...styles.listItem, ...(selected?.id === c.id ? styles.listItemActive : {}) }}
                 onClick={() => setSelected(c)}>
              <div>
                <strong>{c.name}</strong>
                <div style={styles.small}>{c.mappingCount} mapping(s)</div>
              </div>
              <span style={{ color: c.enabled ? '#27ae60' : '#e74c3c' }}>
                {c.enabled ? '●' : '○'}
              </span>
            </div>
          ))}
        </div>

        {/* Right: client detail */}
        {selected && (
          <ClientDetail
            client={selected}
            api={api}
            onRefresh={() => { load(); setSelected(s => ({ ...s })); }}
            onToggle={() => toggleClient(selected)}
            onDelete={() => deleteClient(selected)}
            onNewKey={setNewKey}
          />
        )}
      </div>
    </div>
  );
}

function ClientDetail({ client, api, onRefresh, onToggle, onDelete, onNewKey }) {
  const [mappings, setMappings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editMapping, setEditMapping] = useState(null);

  const loadMappings = async () => {
    const data = await api.get(`/clients/${client.id}`);
    setMappings(data?.mappings || []);
  };

  useEffect(() => { loadMappings(); }, [client.id]);

  const deleteMapping = async (m) => {
    if (!window.confirm(`Delete mapping "${m.label}"?`)) return;
    await api.del(`/clients/${client.id}/mappings/${m.id}`);
    loadMappings();
  };

  return (
    <div style={styles.detailPanel}>
      <div style={styles.detailHeader}>
        <h2 style={{ margin: 0 }}>{client.name}</h2>
        <div>
          <button style={{ ...styles.btnSm, marginRight: 6 }} onClick={onToggle}>
            {client.enabled ? 'Disable' : 'Enable'}
          </button>
          <button style={{ ...styles.btnSm, background: '#e74c3c' }} onClick={onDelete}>Delete</button>
        </div>
      </div>
      <div style={styles.small}>ID: {client.id} · Created: {new Date(client.createdUtc).toLocaleDateString()}</div>

      <h3 style={{ marginTop: 20 }}>Watch Folder Mappings</h3>
      {mappings.map(m => (
        <div key={m.id} style={styles.mappingCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <strong>{m.label}</strong>
            <div>
              <button style={styles.btnSm} onClick={() => { setEditMapping(m); setShowForm(true); }}>Edit</button>
              <button style={{ ...styles.btnSm, background: '#e74c3c', marginLeft: 4 }}
                      onClick={() => deleteMapping(m)}>Remove</button>
            </div>
          </div>
          <div style={styles.small}>
            Server path: {m.serverFolderPath} ·
            Reconcile: {m.reconciliationIntervalMinutes}min ·
            Chunks: {m.chunkSizeMb}MB ·
            Workers: {m.parallelWorkers} ·
            Batch: {m.batchingEnabled ? `✓ (≤${m.batchSmallFileThresholdMb}MB)` : '✗'} ·
            UNC: {m.uncExportEnabled ? `✓ ${m.uncPath}` : '✗'}
          </div>
          {m.uncExportEnabled && (
            <div style={{ marginTop: 8 }}>
              <button style={{ ...styles.btnSm, background: '#f39c12' }}
                      onClick={async () => {
                        if (!window.confirm('Mark all existing files for UNC export? This will queue them for delivery to the UNC path.')) return;
                        await api.post(`/clients/${client.id}/mappings/${m.id}/manifest/enable-unc`);
                        alert('Done — files will export within 5 minutes.');
                      }}>
                ↑ Apply UNC export to existing files
              </button>
            </div>
          )}
        </div>
      ))}

      <button style={{ ...styles.btn, marginTop: 12 }} onClick={() => { setEditMapping(null); setShowForm(true); }}>
        + Add Mapping
      </button>

      {showForm && (
        <MappingForm
          initial={editMapping}
          onSave={async (data) => {
            let res;
            if (editMapping) {
              await api.put(`/clients/${client.id}/mappings/${editMapping.id}`, data);
            } else {
              res = await api.post(`/clients/${client.id}/mappings`, data);
              if (res?.aesKeyBase64) {
                onNewKey({ type: 'mapping', aesKey: res.aesKeyBase64, mappingId: res.id, clientId: client.id });
              }
            }
            setShowForm(false);
            setEditMapping(null);
            loadMappings();
          }}
          onCancel={() => { setShowForm(false); setEditMapping(null); }}
        />
      )}
    </div>
  );
}

function MappingForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({
    label: initial?.label || '',
    serverFolderPath: initial?.serverFolderPath || '',
    reconciliationIntervalMinutes: initial?.reconciliationIntervalMinutes || 5,
    chunkSizeMb: initial?.chunkSizeMb || 5,
    parallelWorkers: initial?.parallelWorkers || 4,
    batchingEnabled: initial?.batchingEnabled || false,
    batchSmallFileThresholdMb: initial?.batchSmallFileThresholdMb || 2,
    batchMaxSizeMb: initial?.batchMaxSizeMb || 20,
    batchMaxWaitSeconds: initial?.batchMaxWaitSeconds || 5,
    uncExportEnabled: initial?.uncExportEnabled || false,
    uncPath: initial?.uncPath || '',
    uncUsername: '',
    uncPassword: '',
  });

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  return (
    <div style={styles.formCard}>
      <h3>{initial ? 'Edit Mapping' : 'New Mapping'}</h3>

      <Section title="General">
        <Field label="Label"><input style={styles.input} value={f.label} onChange={e => set('label', e.target.value)} /></Field>
        <Field label="Server Folder Path"><input style={styles.input} value={f.serverFolderPath} onChange={e => set('serverFolderPath', e.target.value)} placeholder="/data/clients/clientname/" /></Field>
      </Section>

      <Section title="Sync Settings">
        <Field label="Reconcile Interval (min)"><NumInput value={f.reconciliationIntervalMinutes} min={1} max={1440} onChange={v => set('reconciliationIntervalMinutes', v)} /></Field>
        <Field label="Chunk Size (MB)"><NumInput value={f.chunkSizeMb} min={1} max={100} onChange={v => set('chunkSizeMb', v)} /></Field>
        <Field label="Parallel Workers"><NumInput value={f.parallelWorkers} min={1} max={16} onChange={v => set('parallelWorkers', v)} /></Field>
      </Section>

      <Section title="Batching">
        <Field label="Enable Batching">
          <input type="checkbox" checked={f.batchingEnabled} onChange={e => set('batchingEnabled', e.target.checked)} />
        </Field>
        <Field label="Small File Threshold (MB)" dim={!f.batchingEnabled}>
          <NumInput value={f.batchSmallFileThresholdMb} min={1} max={50} onChange={v => set('batchSmallFileThresholdMb', v)} disabled={!f.batchingEnabled} />
        </Field>
        <Field label="Batch Max Size (MB)" dim={!f.batchingEnabled}>
          <NumInput value={f.batchMaxSizeMb} min={1} max={200} onChange={v => set('batchMaxSizeMb', v)} disabled={!f.batchingEnabled} />
        </Field>
        <Field label="Batch Max Wait (sec)" dim={!f.batchingEnabled}>
          <NumInput value={f.batchMaxWaitSeconds} min={1} max={300} onChange={v => set('batchMaxWaitSeconds', v)} disabled={!f.batchingEnabled} />
        </Field>
      </Section>

      <Section title="UNC Export">
        <Field label="Enable UNC Export">
          <input type="checkbox" checked={f.uncExportEnabled} onChange={e => set('uncExportEnabled', e.target.checked)} />
        </Field>
        <Field label="UNC Path" dim={!f.uncExportEnabled}><input style={styles.input} value={f.uncPath} onChange={e => set('uncPath', e.target.value)} placeholder="\\server\share\folder" disabled={!f.uncExportEnabled} /></Field>
        <Field label="UNC Username" dim={!f.uncExportEnabled}><input style={styles.input} value={f.uncUsername} onChange={e => set('uncUsername', e.target.value)} disabled={!f.uncExportEnabled} /></Field>
        <Field label="UNC Password" dim={!f.uncExportEnabled}><input style={styles.input} type="password" value={f.uncPassword} onChange={e => set('uncPassword', e.target.value)} disabled={!f.uncExportEnabled} /></Field>
      </Section>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button style={styles.btn} onClick={() => onSave(f)}>Save Mapping</button>
        <button style={{ ...styles.btn, background: '#7f8c8d' }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Settings Page ─────────────────────────────────────────────────────────────

function SettingsPage() {
  const api = useApi();
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.get('/config').then(setCfg); }, []);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    await api.put('/config', cfg);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (!cfg) return <Spinner />;

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Global Settings</h1>

      <Section title="Sync Defaults">
        <Field label="Default Reconcile Interval (min)"><NumInput value={cfg.defaultReconciliationIntervalMinutes} min={1} max={1440} onChange={v => set('defaultReconciliationIntervalMinutes', v)} /></Field>
        <Field label="Default Chunk Size (MB)"><NumInput value={cfg.defaultChunkSizeMb} min={1} max={100} onChange={v => set('defaultChunkSizeMb', v)} /></Field>
        <Field label="Default Parallel Workers"><NumInput value={cfg.defaultParallelWorkers} min={1} max={16} onChange={v => set('defaultParallelWorkers', v)} /></Field>
      </Section>

      <Section title="Log Retention">
        <Field label="Retain Logs (days)"><NumInput value={cfg.logRetentionDays} min={7} max={3650} onChange={v => set('logRetentionDays', v)} /></Field>
      </Section>

      <Section title="Active Directory / LDAP">
        <Field label="Enable AD Auth"><input type="checkbox" checked={cfg.ldapEnabled} onChange={e => set('ldapEnabled', e.target.checked)} /></Field>
        <Field label="AD Server Host"><input style={styles.input} value={cfg.ldapHost || ''} onChange={e => set('ldapHost', e.target.value)} disabled={!cfg.ldapEnabled} /></Field>
        <Field label="LDAP Port"><NumInput value={cfg.ldapPort || 389} min={1} max={65535} onChange={v => set('ldapPort', v)} disabled={!cfg.ldapEnabled} /></Field>
        <Field label="Use SSL"><input type="checkbox" checked={cfg.ldapUseSsl || false} onChange={e => set('ldapUseSsl', e.target.checked)} disabled={!cfg.ldapEnabled} /></Field>
        <Field label="Base DN"><input style={styles.input} value={cfg.ldapBaseDn || ''} onChange={e => set('ldapBaseDn', e.target.value)} placeholder="DC=corp,DC=example,DC=com" disabled={!cfg.ldapEnabled} /></Field>
      </Section>

      <button style={styles.btn} onClick={save}>Save Settings</button>
      {saved && <span style={{ marginLeft: 12, color: '#27ae60' }}>✓ Saved</span>}
    </div>
  );
}

// ── Shared Components ─────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={styles.sectionHeader}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children, dim }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
      <label style={{ ...styles.fieldLabel, opacity: dim ? 0.4 : 1 }}>{label}</label>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function NumInput({ value, min, max, onChange, disabled }) {
  return (
    <input type="number" style={{ ...styles.input, width: 100, marginBottom: 0 }}
           value={value} min={min} max={max} disabled={disabled}
           onChange={e => onChange(parseInt(e.target.value) || min)} />
  );
}

function KeyDisplay({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={styles.small}>{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly style={{ ...styles.input, fontFamily: 'monospace', fontSize: 12, marginBottom: 0, flex: 1 }} value={value} />
        <button style={styles.btnSm} onClick={() => { navigator.clipboard.writeText(value); setCopied(true); }}>
          {copied ? '✓' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function Spinner() {
  return <div style={{ padding: 20, color: '#7f8c8d' }}>Loading...</div>;
}

// ── Root App ──────────────────────────────────────────────────────────────────

function App() {
  const [page, setPage] = useState('clients');
  const { isAuthed } = useAuth();

  if (!isAuthed) return <LoginPage />;

  return (
    <Shell page={page} setPage={setPage}>
      {page === 'clients' && <ClientsPage />}
      {page === 'files' && <FileManagerPage />}
      {page === 'settings' && <SettingsPage />}
    </Shell>
  );
}

// ── File Manager Page ─────────────────────────────────────────────────────────

function FileManagerPage() {
  const api = useApi();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [view, setView] = useState('discrepancies'); // 'discrepancies' | 'all' | 'today'
  const [discrepancies, setDiscrepancies] = useState(null);
  const [allFiles, setAllFiles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteFiles, setDeleteFiles] = useState(true);
  const [inventoryAge, setInventoryAge] = useState(null);
  const [lastReported, setLastReported] = useState(null);

  useEffect(() => {
    api.get('/clients').then(data => setClients(data || []));
  }, []);

  const loadData = async (clientId, mappingId, currentView) => {
    setLoading(true);
    setSelected(new Set());
    try {
      if (currentView === 'discrepancies' || currentView === undefined) {
        const data = await api.get(`/clients/${clientId}/mappings/${mappingId}/manifest/discrepancies`);
        setDiscrepancies(data?.discrepancies || []);
        setInventoryAge(data?.inventoryAge);
        setLastReported(data?.lastReported);
      } else {
        const data = await api.get(`/clients/${clientId}/mappings/${mappingId}/manifest`);
        setAllFiles(data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const selectClient = async (c) => {
    const full = await api.get(`/clients/${c.id}`);
    setSelectedClient(full);
    setSelectedMapping(null);
    setDiscrepancies(null);
    setAllFiles([]);
  };

  const selectMapping = (m) => {
    setSelectedMapping(m);
    loadData(selectedClient.id, m.id, view);
  };

  const switchView = (v) => {
    setView(v);
    setSelected(new Set());
    setSearch('');
    if (selectedMapping) loadData(selectedClient.id, selectedMapping.id, v);
  };

  const toggleSelect = (path) => {
    const s = new Set(selected);
    s.has(path) ? s.delete(path) : s.add(path);
    setSelected(s);
  };

  const toggleSelectAll = (items) => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(f => f.relativePath || f.RelativePath)));
  };

  const handleDelete = async () => {
    if (selected.size === 0) return;
    await api.post(
      `/clients/${selectedClient.id}/mappings/${selectedMapping.id}/manifest/bulk-delete`,
      { paths: [...selected], deleteFiles }
    );
    setConfirmDelete(false);
    setSelected(new Set());
    loadData(selectedClient.id, selectedMapping.id, view);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1048576) return `${(bytes/1024).toFixed(1)}KB`;
    return `${(bytes/1048576).toFixed(1)}MB`;
  };

  const statusBadge = (status) => {
    const map = {
      ServerOnly: { label: 'Server Only', color: '#e67e22', bg: '#fef5ec' },
      ClientOnly: { label: 'Client Only', color: '#2980b9', bg: '#ebf5fb' },
      HashMismatch: { label: 'Hash Mismatch', color: '#8e44ad', bg: '#f5eef8' },
    };
    const s = map[status] || { label: status, color: '#7f8c8d', bg: '#f0f0f0' };
    return (
      <span style={{ background: s.bg, color: s.color, borderRadius: 4,
                     padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
        {s.label}
      </span>
    );
  };

  // Filter for all-files view
  const today = new Date(); today.setHours(0,0,0,0);
  const filteredAll = allFiles.filter(f => {
    if (search && !f.relativePath.toLowerCase().includes(search.toLowerCase())) return false;
    if (view === 'today') return new Date(f.lastModifiedUtc) >= today;
    return true;
  });

  const filteredDisc = (discrepancies || []).filter(f => {
    const path = f.relativePath || f.RelativePath || '';
    return !search || path.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>File Manager</h1>

      <div style={styles.twoCol}>
        {/* Left: client + mapping selector */}
        <div style={{ width: 240, flexShrink: 0 }}>
          <div style={styles.sectionHeader}>Clients</div>
          {clients.map(c => (
            <div key={c.id}
                 style={{ ...styles.listItem, ...(selectedClient?.id === c.id ? styles.listItemActive : {}) }}
                 onClick={() => selectClient(c)}>
              {c.name}
            </div>
          ))}
          {selectedClient?.mappings?.length > 0 && (
            <>
              <div style={{ ...styles.sectionHeader, marginTop: 16 }}>Mappings</div>
              {selectedClient.mappings.map(m => (
                <div key={m.id}
                     style={{ ...styles.listItem, ...(selectedMapping?.id === m.id ? styles.listItemActive : {}) }}
                     onClick={() => selectMapping(m)}>
                  {m.label}
                </div>
              ))}
            </>
          )}
        </div>

        {/* Right: file view */}
        <div style={{ flex: 1 }}>
          {!selectedMapping ? (
            <div style={{ color: '#7f8c8d', padding: 20 }}>Select a client and mapping to view files.</div>
          ) : (
            <>
              {/* View switcher */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                {['discrepancies', 'all', 'today'].map(v => (
                  <button key={v} onClick={() => switchView(v)}
                          style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13,
                                   background: view === v ? '#3498db' : '#e8edf2',
                                   color: view === v ? '#fff' : '#555', fontWeight: view === v ? 600 : 400 }}>
                    {v === 'discrepancies' ? '⚠ Differences' : v === 'all' ? '📋 All Files' : '📅 Today'}
                  </button>
                ))}
                <div style={{ flex: 1 }} />
                <input style={{ ...styles.input, marginBottom: 0, width: 200 }}
                       placeholder="Search..." value={search}
                       onChange={e => setSearch(e.target.value)} />
                <button style={{ ...styles.btnSm, padding: '6px 12px' }}
                        onClick={() => loadData(selectedClient.id, selectedMapping.id, view)}>
                  ↻
                </button>
              </div>

              {/* Inventory age warning */}
              {view === 'discrepancies' && lastReported && (
                <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 10,
                              background: inventoryAge && inventoryAge > 600 ? '#fef5ec' : '#eafaf1',
                              color: inventoryAge && inventoryAge > 600 ? '#e67e22' : '#27ae60',
                              fontSize: 12 }}>
                  {lastReported
                    ? `Client inventory last reported: ${new Date(lastReported).toLocaleString()}`
                    : 'No client inventory yet — waiting for next reconciliation cycle'}
                </div>
              )}

              {loading ? <Spinner /> : view === 'discrepancies' ? (
                // ── Discrepancy view ──────────────────────────────────────────
                <>
                  {filteredDisc.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#27ae60', fontSize: 16 }}>
                      ✓ No discrepancies found — client and server are in sync
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: '#7f8c8d' }}>
                          {filteredDisc.length} discrepanc{filteredDisc.length !== 1 ? 'ies' : 'y'}
                          {selected.size > 0 && ` · ${selected.size} selected`}
                        </span>
                        {selected.size > 0 && (
                          <button style={{ ...styles.btn, background: '#e74c3c', padding: '6px 12px', fontSize: 12 }}
                                  onClick={() => setConfirmDelete(true)}>
                            Remove {selected.size} from manifest
                          </button>
                        )}
                      </div>
                      <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f7f9fc', borderBottom: '1px solid #e0e6ed' }}>
                              <th style={{ padding: '10px 12px', width: 32 }}>
                                <input type="checkbox"
                                       checked={selected.size === filteredDisc.filter(f => f.status === 'ServerOnly').length && filteredDisc.filter(f => f.status === 'ServerOnly').length > 0}
                                       onChange={() => toggleSelectAll(filteredDisc.filter(f => f.status === 'ServerOnly'))} />
                              </th>
                              <th style={{ padding: '10px 12px', textAlign: 'left' }}>Path</th>
                              <th style={{ padding: '10px 12px', textAlign: 'center', width: 130 }}>Status</th>
                              <th style={{ padding: '10px 12px', textAlign: 'right', width: 80 }}>Size</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', width: 150 }}>Modified</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredDisc.map((f, i) => {
                              const path = f.relativePath || f.RelativePath;
                              const canDelete = f.status === 'ServerOnly';
                              return (
                                <tr key={path}
                                    style={{ background: selected.has(path) ? '#ebf5fb'
                                             : i % 2 === 0 ? '#fff' : '#fafbfc',
                                             borderBottom: '1px solid #f0f0f0',
                                             cursor: canDelete ? 'pointer' : 'default' }}
                                    onClick={() => canDelete && toggleSelect(path)}>
                                  <td style={{ padding: '8px 12px' }}>
                                    {canDelete && (
                                      <input type="checkbox" checked={selected.has(path)}
                                             onChange={() => toggleSelect(path)}
                                             onClick={e => e.stopPropagation()} />
                                    )}
                                  </td>
                                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                                    {path}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                    {statusBadge(f.status)}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', color: '#7f8c8d' }}>
                                    {formatSize(f.sizeBytes || f.SizeBytes)}
                                  </td>
                                  <td style={{ padding: '8px 12px', color: '#7f8c8d', fontSize: 12 }}>
                                    {new Date(f.serverModified || f.clientModified || f.ServerModified || f.ClientModified).toLocaleString()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 12, color: '#7f8c8d' }}>
                        ℹ️ Only <strong>Server Only</strong> entries can be removed from manifest.
                        Client Only files will upload on next reconciliation.
                      </div>
                    </>
                  )}
                </>
              ) : (
                // ── All files / Today view ────────────────────────────────────
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#7f8c8d' }}>
                      {filteredAll.length} file{filteredAll.length !== 1 ? 's' : ''}
                      {selected.size > 0 && ` · ${selected.size} selected`}
                    </span>
                    {selected.size > 0 && (
                      <button style={{ ...styles.btn, background: '#e74c3c', padding: '6px 12px', fontSize: 12 }}
                              onClick={() => setConfirmDelete(true)}>
                        Delete {selected.size}
                      </button>
                    )}
                  </div>
                  <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f7f9fc', borderBottom: '1px solid #e0e6ed' }}>
                          <th style={{ padding: '10px 12px', width: 32 }}>
                            <input type="checkbox"
                                   checked={selected.size === filteredAll.length && filteredAll.length > 0}
                                   onChange={() => toggleSelectAll(filteredAll)} />
                          </th>
                          <th style={{ padding: '10px 12px', textAlign: 'left' }}>Path</th>
                          <th style={{ padding: '10px 12px', textAlign: 'right', width: 80 }}>Size</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center', width: 80 }}>On Disk</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center', width: 100 }}>UNC</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', width: 150 }}>Modified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAll.map((f, i) => (
                          <tr key={f.relativePath}
                              style={{ background: selected.has(f.relativePath) ? '#ebf5fb'
                                       : i % 2 === 0 ? '#fff' : '#fafbfc',
                                       borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                              onClick={() => toggleSelect(f.relativePath)}>
                            <td style={{ padding: '8px 12px' }}>
                              <input type="checkbox" checked={selected.has(f.relativePath)}
                                     onChange={() => toggleSelect(f.relativePath)}
                                     onClick={e => e.stopPropagation()} />
                            </td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                              {f.relativePath}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: '#7f8c8d' }}>
                              {formatSize(f.sizeBytes)}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                              <span style={{ color: f.existsOnDisk ? '#27ae60' : '#e74c3c' }}>
                                {f.existsOnDisk ? '✓' : '✗'}
                              </span>
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11 }}>
                              {!f.uncExportEnabled ? <span style={{ color: '#bdc3c7' }}>—</span>
                                : f.uncExported ? <span style={{ color: '#27ae60' }}>✓</span>
                                : f.uncExportError ? <span style={{ color: '#e74c3c' }} title={f.uncExportError}>⚠ {f.uncExportAttempts}x</span>
                                : <span style={{ color: '#f39c12' }}>⏳</span>}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#7f8c8d', fontSize: 12 }}>
                              {new Date(f.lastModifiedUtc).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                        {filteredAll.length === 0 && (
                          <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#7f8c8d' }}>
                            No files found.
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h3 style={{ marginTop: 0 }}>Confirm Remove from Manifest</h3>
            <p>Remove <strong>{selected.size}</strong> entr{selected.size !== 1 ? 'ies' : 'y'} from the server manifest?</p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={deleteFiles} onChange={e => setDeleteFiles(e.target.checked)} />
              Also delete the encrypted files from disk
            </label>
            <p style={{ color: '#7f8c8d', fontSize: 13 }}>
              {deleteFiles
                ? 'Encrypted files will be permanently deleted from server storage.'
                : 'Encrypted files stay on disk. Client will NOT re-upload (entries removed from manifest).'}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...styles.btn, background: '#e74c3c' }} onClick={handleDelete}>Confirm</button>
              <button style={{ ...styles.btn, background: '#7f8c8d' }} onClick={() => setConfirmDelete(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Root() {
  return <AuthProvider><App /></AuthProvider>;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  loginWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f0f2f5' },
  loginCard: { background: '#fff', borderRadius: 12, padding: 40, width: 360, boxShadow: '0 4px 24px rgba(0,0,0,0.1)' },
  loginLogo: { width: 180, marginBottom: 16, display: 'block' },
  loginTitle: { marginBottom: 20, color: '#2c3e50', fontSize: 22 },
  shell: { display: 'flex', minHeight: '100vh', fontFamily: 'system-ui, sans-serif', color: '#2c3e50' },
  sidebar: { width: 220, background: '#2c3e50', display: 'flex', flexDirection: 'column', padding: '20px 0' },
  sidebarLogo: { width: 140, margin: '0 auto 8px', display: 'block', filter: 'invert(1)' },
  sidebarTitle: { color: '#ecf0f1', textAlign: 'center', fontWeight: 700, fontSize: 14, marginBottom: 20, letterSpacing: 1 },
  navItem: { padding: '10px 20px', color: '#bdc3c7', cursor: 'pointer', borderRadius: 4, margin: '2px 8px', transition: 'background 0.15s' },
  navActive: { background: '#3498db', color: '#fff' },
  navUser: { padding: '8px 20px', color: '#7f8c8d', fontSize: 13 },
  sidebarFooter: { marginTop: 'auto' },
  main: { flex: 1, background: '#f7f9fc', overflowY: 'auto' },
  page: { padding: 32, maxWidth: 1100 },
  pageTitle: { fontSize: 26, fontWeight: 700, marginBottom: 24, color: '#2c3e50' },
  twoCol: { display: 'flex', gap: 24 },
  listPanel: { width: 280, flexShrink: 0 },
  detailPanel: { flex: 1, background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  listItem: { padding: '12px 14px', background: '#fff', borderRadius: 8, marginBottom: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  listItemActive: { background: '#ebf5fb', borderLeft: '3px solid #3498db' },
  addRow: { display: 'flex', marginBottom: 10, alignItems: 'center' },
  mappingCard: { background: '#f7f9fc', borderRadius: 8, padding: 12, marginBottom: 8 },
  formCard: { background: '#f7f9fc', borderRadius: 10, padding: 20, marginTop: 16 },
  sectionHeader: { fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: '#7f8c8d', marginBottom: 10, borderBottom: '1px solid #e0e6ed', paddingBottom: 4 },
  fieldLabel: { width: 220, fontSize: 13, color: '#555', flexShrink: 0 },
  input: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d5dbe3', fontSize: 13, marginBottom: 6, boxSizing: 'border-box' },
  btn: { background: '#3498db', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 20px', cursor: 'pointer', fontSize: 14 },
  btnSm: { background: '#3498db', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontSize: 12 },
  small: { fontSize: 12, color: '#7f8c8d', marginTop: 2 },
  error: { color: '#e74c3c', fontSize: 13, marginBottom: 10 },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalCard: { background: '#fff', borderRadius: 12, padding: 32, width: 480, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' },
};
